import React, { useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaUserGraduate, FaProjectDiagram, FaStar, FaCogs } from 'react-icons/fa';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const AnimatedNumber = ({ end, suffix, decimals = 0 }) => {
    const [startAnimation, setStartAnimation] = useState(false);
    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: true,
    });

    useEffect(() => {
        if (inView) {
            setStartAnimation(true);
        }
    }, [inView]);

    return (
        <h3 className="stat-value-new" ref={ref}>
            {startAnimation ? <CountUp end={end} duration={2.5} decimals={decimals} /> : '0'}
            {suffix}
        </h3>
    );
};

const Hero2 = () => {
    const cardsRef = useRef([]);
    useEffect(() => {
        AOS.init({ duration: 1000, offset: 150 });

        cardsRef.current.forEach(card => {
            if (!card) return;
            card.addEventListener('mousemove', (e) => {
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                card.style.setProperty('--mouse-x', `${x}px`);
                card.style.setProperty('--mouse-y', `${y}px`);
            });
        });
    }, []);

    const stats = [
        { icon: <FaProjectDiagram />, value: 50, suffix: '+', label: 'Projects Delivered', desc: 'web, mobile, UI/UX & marketing solutions', accentColor: '#a04ff4' },
        { icon: <FaStar />, value: 4.9, suffix: '', label: 'Average Client Rating', desc: 'based on client feedback & reviews', accentColor: '#ffb428', decimals: 1 },
        { icon: <FaUserGraduate />, value: 150, suffix: '+', label: 'Interns Trained', desc: 'through industrial project experience', accentColor: '#3b82f6' },
        { icon: <FaCogs />, value: 6, suffix: '+', label: 'Services Offered', desc: 'full-stack web, app, design, marketing & more', accentColor: '#f78ef6' },
    ];

    return (
        <>
            <section className="stats-section-new">
                <div className="container text-center">
                    <div className="stats-header" data-aos="fade-up">
                        <span className="stats-badge-label">OUR IMPACT</span>
                        <h2 className="stats-heading-new">
                            Numbers That <span className="stats-gradient-text">Speak for Themselves</span>
                        </h2>
                    </div>
                    <div className="row g-4 justify-content-center">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className="col-lg-3 col-md-6"
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                            >
                                <div
                                    className="stat-card-new"
                                    ref={el => cardsRef.current[index] = el}
                                    style={{ '--accent': stat.accentColor }}
                                >
                                    <div className="stat-card-accent"></div>
                                    <div className="stat-icon-new">{stat.icon}</div>
                                    <AnimatedNumber end={stat.value} suffix={stat.suffix} decimals={stat.decimals || 0} />
                                    <p className="stat-label-new">{stat.label}</p>
                                    <p className="stat-desc">{stat.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <style>{`
                /* =============================================
                   REDESIGNED STATS SECTION
                   ============================================= */

                .stats-section-new {
                    padding: 100px 0;
                    background-color: #0c001f;
                    color: #fff;
                    position: relative;
                }

                .stats-header {
                    margin-bottom: 60px;
                }

                .stats-badge-label {
                    display: inline-block;
                    font-size: 0.75rem;
                    font-weight: 700;
                    letter-spacing: 3px;
                    color: #ffb428;
                    background: rgba(255, 180, 40, 0.1);
                    padding: 7px 20px;
                    border-radius: 50px;
                    border: 1px solid rgba(255, 180, 40, 0.2);
                    margin-bottom: 20px;
                    text-transform: uppercase;
                }

                .stats-heading-new {
                    font-size: 3.2rem;
                    font-weight: 800;
                    color: #fff;
                    letter-spacing: -1px;
                }

                .stats-gradient-text {
                    background: linear-gradient(90deg, #a04ff4, #ffb428);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                /* ─── Stat Cards ─── */
                .stat-card-new {
                    --mouse-x: 50%;
                    --mouse-y: 50%;
                    --accent: #a04ff4;
                    background: rgba(255, 255, 255, 0.025);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 20px;
                    padding: 40px 24px 32px;
                    height: 100%;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    backdrop-filter: blur(8px);
                }

                /* Gradient top accent bar */
                .stat-card-accent {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, var(--accent), transparent);
                    opacity: 0.6;
                    transition: opacity 0.3s ease;
                }

                /* Mouse-follow aurora */
                .stat-card-new::before {
                    content: "";
                    position: absolute;
                    height: 180px;
                    width: 180px;
                    left: var(--mouse-x);
                    top: var(--mouse-y);
                    transform: translate(-50%, -50%);
                    background: radial-gradient(circle, color-mix(in srgb, var(--accent) 15%, transparent) 0%, transparent 70%);
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    pointer-events: none;
                }

                .stat-card-new:hover {
                    transform: translateY(-8px);
                    border-color: color-mix(in srgb, var(--accent) 40%, transparent);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
                }

                .stat-card-new:hover .stat-card-accent {
                    opacity: 1;
                }

                .stat-card-new:hover::before {
                    opacity: 1;
                }

                .stat-icon-new {
                    font-size: 1.8rem;
                    margin-bottom: 18px;
                    color: var(--accent);
                    display: flex;
                    justify-content: center;
                }

                .stat-value-new {
                    font-size: 3.2rem;
                    font-weight: 800;
                    color: #fff;
                    margin-bottom: 8px;
                    letter-spacing: -1px;
                }

                .stat-label-new {
                    margin: 0;
                    color: #e0e0e0;
                    font-size: 1rem;
                    font-weight: 600;
                    text-align: center;
                }

                .stat-desc {
                    margin: 6px 0 0;
                    color: #6a6a6a;
                    font-size: 0.82rem;
                    font-weight: 400;
                    text-align: center;
                    font-style: italic;
                }

                /* ─── Responsive ─── */
                @media (max-width: 991px) {
                    .stats-heading-new {
                        font-size: 2.6rem;
                    }
                }

                @media (max-width: 768px) {
                    .stats-section-new {
                        padding: 80px 15px;
                    }
                    .stats-heading-new {
                        font-size: 2.2rem;
                    }
                    .stat-value-new {
                        font-size: 2.8rem;
                    }
                }
            `}</style>
        </>
    );
};

export default Hero2;